import Head from 'next/head';
import Script from 'next/script';

import type { ShopConfig, Page } from 'domain-model/types/supplier-gateway';

export interface tempOrganization {
  name: string;
}

export interface HeadProps {
  shopConfig: ShopConfig;
  asPath: string;
  pageConfig?: Page;
}

export interface ImageAndDescriptionProps {
  description: string;
  image: string;
  title: string;
}

export const HeadDescriptionElements = ({
  description,
  title,
  image,
}: ImageAndDescriptionProps) => {
  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={image} />

        {/* <!-- twitter cards --> */}
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={image} />
      </Head>
    </>
  );
};

const HeadElement = ({ asPath, shopConfig, pageConfig }: HeadProps) => {
  const { organization, theme, googleAnalyticsKey, clarityKey, baseUrl } =
    shopConfig;
  const description = pageConfig?.description || shopConfig.description;

  const isProductionCluster = true;
  // `G-94J1HWYKE3`
  return (
    <>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="preconnect" href="https://www.google-analytics.com" />
        <link rel="preconnect" href="https://www.googletagmanager.com" />

        {/* <link
          rel="stylesheet"
          href={theme === 'privabTheme' ? '/privab.css' : '/flaivy.css'}
        /> */}
        <link
          rel="stylesheet"
          href={
            theme === 'privabTheme' ? '/privab-v1.03.css' : '/flaivy-v0.82.css'
          }
        />
        <link rel="icon" type="image/x-icon" href={shopConfig.favicon} />
        <link
          rel="canonical"
          href={`https://${baseUrl}${asPath.split('?')[0]}`.replace(
            /([^:]\/)\/+/g,
            '$1',
          )}
        />
        <meta property="og:type" content="website" />
        {/* <!-- twitter cards --> */}
        <meta name="twitter:card" content="summary_large_image" />
      </Head>
      {isProductionCluster && googleAnalyticsKey && (
        <>
          <Script
            src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsKey}`}
          />
          <Script id="google-analytics">
            {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
  
          gtag('config', '${googleAnalyticsKey}');
          `}
          </Script>
        </>
      )}
      {/* https://stackoverflow.com/questions/72210974/microsoft-clarity-not-working-with-next-js-react-js-project */}
      {isProductionCluster && clarityKey && (
        <>
          <Script type="text/javascript" id="clarityscripttag">
            {`(function(c,l,a,r,i,t,y){
              c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
              t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
              y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
          })(window, document, "clarity", "script", "${clarityKey}");
          `}
          </Script>
        </>
      )}
      <HeadDescriptionElements
        description={description || ''}
        image={shopConfig.logo || ''}
        title={pageConfig?.title || `${organization?.name} Shop`}
      />
    </>
  );
};

export default HeadElement;
